import React, { useState, useEffect, useRef } from 'react';
import { tags } from '../utils/api';
import { motion, AnimatePresence } from 'framer-motion';
import TagBadge from './Documents/atoms/TagBadge';
import CreateTagModal from './CreateTagModal';

const TagSelector = ({ selectedTags = [], onChange, className, tagCounts = {}, totalCoins = 0 }) => {
  const [userTags, setUserTags] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTagInitialName, setNewTagInitialName] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const fetchedTags = await tags.getAll();
        setUserTags(fetchedTags);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTags();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCreateTag = (newTag) => {
    setUserTags([...userTags, newTag]);
    setSearchTerm('');
    setShowDropdown(false);
    handleTagSelect(newTag);
  };

  const handleTagSelect = (tag) => {
    const isSelected = selectedTags.some(t => t.id === tag.id);
    const newSelectedTags = isSelected
      ? selectedTags.filter(t => t.id !== tag.id)
      : [...selectedTags, tag];
    
    onChange(newSelectedTags);
    setSearchTerm('');
  };

  const filteredTags = userTags.filter(tag =>
    tag.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {error && (
        <div className="absolute top-0 left-0 right-0 -mt-6 bg-red-50 text-red-600 p-2 rounded-md text-sm">
          {error}
          <button 
            onClick={() => setError(null)}
            className="float-right text-red-600 hover:text-red-800"
          >
            ×
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-1 min-h-[2.25rem] py-1 px-2 bg-white border border-gray-300 rounded-md cursor-text w-full focus-within:ring-indigo-500 focus-within:border-indigo-500" onClick={() => setShowDropdown(true)}>
        {selectedTags.map(tag => (
          <motion.div
            key={tag.id}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              setShowDropdown(true);
            }}
          >
            <TagBadge
              name={tag.name}
              color={tag.color}
              onRemove={() => handleTagSelect(tag)}
            />
          </motion.div>
        ))}
        
        <input
          type="text"
          id="tags"
          name="tags"
          className="flex-[0_1_150px] outline-none bg-transparent border-0 text-sm focus:ring-0 py-1"
          placeholder={selectedTags.length === 0 ? "Add tags" : ""}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
        />
      </div>

      <AnimatePresence>
        {showDropdown && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md overflow-hidden"
          >
            <div className="max-h-60 overflow-auto">
              {loading ? (
                <div className="p-4 text-center">
                  <div className="animate-spin inline-block w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full"></div>
                </div>
              ) : (
                <>
                  {searchTerm ? (
                    // When searching
                    filteredTags.length > 0 ? (
                      // Show matching tags
                      <div className="py-1">
                        {filteredTags.map(tag => (
                          <div
                            key={tag.id}
                            onClick={() => handleTagSelect(tag)}
                            className={`px-4 py-2 text-sm cursor-pointer flex items-center justify-between
                              ${selectedTags.some(t => t.id === tag.id)
                                ? 'bg-indigo-50 text-indigo-900'
                                : 'hover:bg-gray-100'
                              }`}
                          >
                            <span className="flex items-center justify-between w-full">
                              <TagBadge
                                name={tag.name}
                                color={tag.color}
                                interactive={false}
                              />
                              {tagCounts[tag.id] !== undefined && (
                                <span className="text-xs text-gray-500">
                                  {tagCounts[tag.id]} of {totalCoins} {totalCoins === 1 ? 'coin' : 'coins'}
                                </span>
                              )}
                            </span>
                            {selectedTags.some(t => t.id === tag.id) && (
                              <svg className="h-4 w-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      // Show create option for no matches
                      <div className="p-4">
                        <div className="text-sm text-gray-500 mb-2">No matching tags</div>
                        <button
                          onClick={() => {
                            setNewTagInitialName(searchTerm);
                            setShowCreateModal(true);
                          }}
                          className="w-full px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700"
                        >
                          Create "{searchTerm}"
                        </button>
                      </div>
                    )
                  ) : (
                    // When field is empty, show both existing tags and create option
                    <>
                      {userTags.length > 0 && (
                        <div className="py-1">
                          {userTags.map(tag => (
                            <div
                              key={tag.id}
                              onClick={() => handleTagSelect(tag)}
                              className={`px-4 py-2 text-sm cursor-pointer flex items-center justify-between
                                ${selectedTags.some(t => t.id === tag.id)
                                  ? 'bg-indigo-50 text-indigo-900'
                                  : 'hover:bg-gray-100'
                                }`}
                            >
                              <span className="flex items-center justify-between w-full">
                                <TagBadge
                                  name={tag.name}
                                  color={tag.color}
                                  interactive={false}
                                />
                                {tagCounts[tag.id] !== undefined && (
                                  <span className="text-xs text-gray-500">
                                    {tagCounts[tag.id]} of {totalCoins} {totalCoins === 1 ? 'coin' : 'coins'}
                                  </span>
                                )}
                              </span>
                              {selectedTags.some(t => t.id === tag.id) && (
                                <svg className="h-4 w-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="p-4 border-t border-gray-200">
                        <button
                          onClick={() => {
                            setNewTagInitialName('');
                            setShowCreateModal(true);
                          }}
                          className="w-full px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700"
                        >
                          Create New Tag
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <CreateTagModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onTagCreated={handleCreateTag}
        initialName={newTagInitialName}
      />
    </div>
  );
};

export default TagSelector;
